/*
 * Strato Lab 3D — Central content source (Italian).
 * Keep all copy here so sections stay clean and future CMS/API wiring is easy.
 */

export const NAV_LINKS = [
  { label: "Servizi", to: "/servizi" },
  { label: "Materiali", to: "/materiali" },
  { label: "Come funziona", to: "/come-funziona" },
  { label: "Metodo", to: "/metodo" },
  { label: "Chi siamo", to: "/chi-siamo" },
  { label: "Contatti", to: "/contatti" },
];

export const HERO = {
  microLabels: ["FDM / PLA", "Analisi tecnica", "Produzione controllata"],
  headline: "Dal file al pezzo reale.",
  support:
    "Analisi tecnica umana, produzione controllata e consegna chiara. Nessun catalogo: solo il tuo pezzo, prodotto bene.",
  primaryCta: "Avvia il preventivo",
  secondaryCta: "Scopri il processo",
  ctaNote: "Preventivo tecnico entro 12 ore lavorative.",
};

export const ENTRY_PATHS = [
  {
    id: "file",
    index: "01",
    label: "HAI GIÀ IL MODELLO",
    title: "Ho un file 3D",
    description:
      "Carichi STL, STEP, STP o 3MF. Lo analizziamo, verifichiamo stampabilità e tolleranze, poi ti proponiamo il preventivo.",
    hint: "STL · STEP · STP · 3MF",
  },
  {
    id: "idea",
    index: "02",
    label: "PARTIAMO DALL'IDEA",
    title: "Ho un'idea o un disegno",
    description:
      "Uno schizzo, una foto o un disegno tecnico. Ti aiutiamo a trasformarlo in un modello pronto per la produzione.",
    hint: "Schizzo · Foto · Disegno",
  },
  {
    id: "replace",
    index: "03",
    label: "RICOSTRUZIONE",
    title: "Devo rifare un pezzo esistente",
    description:
      "Un componente rotto o fuori produzione. Rileviamo geometria e misure e lo ricostruiamo fedelmente.",
    hint: "Misure · Rilievo · Ricostruzione",
  },
];

export const APPLICATIONS = [
  {
    id: "prototype",
    glyph: "prototype",
    to: "/servizi/prototipi-funzionali",
    title: "Prototipi funzionali",
    description:
      "Iterazioni rapide per validare forma, incastri ed ergonomia prima della produzione.",
    meta: "ITERAZIONE",
  },
  {
    id: "techComponent",
    glyph: "techComponent",
    to: "/servizi/componenti-tecnici",
    title: "Componenti tecnici",
    description:
      "Parti funzionali con tolleranze verificate e geometrie precise.",
    meta: "FUNZIONALE",
  },
  {
    id: "sparePart",
    glyph: "sparePart",
    to: "/servizi/ricambi-adattamenti",
    title: "Ricambi e adattamenti",
    description:
      "Pezzi fuori produzione ricostruiti fedelmente, o adattati a nuove esigenze.",
    meta: "RICOSTRUZIONE",
  },
  {
    id: "bracket",
    glyph: "bracket",
    to: "/servizi/supporti-dime",
    title: "Supporti e staffe",
    description: "Staffe, dime e fissaggi dedicati per ogni installazione.",
    meta: "FISSAGGIO",
  },
  {
    id: "enclosure",
    glyph: "enclosure",
    to: "/servizi/contenitori-gusci",
    title: "Contenitori e gusci",
    description: "Custodie, gusci e alloggiamenti su misura per l'elettronica.",
    meta: "GUSCIO",
  },
  {
    id: "series",
    glyph: "series",
    to: "/servizi/piccole-serie",
    title: "Piccole serie",
    description: "Lotti ripetibili con qualità costante pezzo dopo pezzo.",
    meta: "LOTTO",
  },
];

export const PROCESS_STEPS = [
  {
    index: "01",
    title: "Invio",
    meta: "RICHIESTA",
    description:
      "Invii il file 3D o descrivi il pezzo. Ogni richiesta riceve un riferimento univoco.",
  },
  {
    index: "02",
    title: "Analisi tecnica",
    meta: "CHECK TECNICO",
    description:
      "Un tecnico verifica geometria, spessori, orientamento e stampabilità. Nessun automatismo cieco.",
  },
  {
    index: "03",
    title: "Preventivo",
    meta: "VALUTAZIONE",
    description:
      "Ricevi tempi, materiale e costo in modo chiaro. In questa fase non parte alcuna produzione.",
  },
  {
    index: "04",
    title: "Conferma e pagamento",
    meta: "APPROVAZIONE",
    description:
      "La produzione inizia solo dopo la tua conferma del preventivo e, ove previsto, il pagamento.",
  },
  {
    index: "05",
    title: "Produzione",
    meta: "CONTROLLO DIMENSIONALE",
    description:
      "Stampa su sistemi controllati, con parametri coerenti e ripetibili.",
  },
  {
    index: "06",
    title: "Consegna",
    meta: "IMBALLO PROTETTIVO",
    description:
      "Controllo finale, finitura base e spedizione protetta fino a destinazione.",
  },
];

export const PRODUCTION = {
  microLabel: "COME PRODUCIAMO",
  title: "Dal file pronto al pezzo finito.",
  description:
    "Utilizziamo sistemi Bambu Lab e filamenti originali Bambu Lab, con parametri controllati e documentazione tecnica del materiale. Ogni pezzo attraversa cinque fasi definite.",
  phases: [
    {
      id: "file",
      diagram: "file",
      index: "01",
      title: "Preparazione file",
      description:
        "Verifica di geometria, spessori e integrità della mesh. Il modello viene reso pronto per la produzione.",
    },
    {
      id: "orientation",
      diagram: "orientation",
      index: "02",
      title: "Orientamento",
      description:
        "Scelta dell'orientamento ottimale per resistenza, finitura e riduzione dei supporti.",
    },
    {
      id: "slicing",
      diagram: "slicing",
      index: "03",
      title: "Slicing",
      description:
        "Generazione dei layer con parametri calibrati: altezza layer, riempimento e pareti.",
    },
    {
      id: "print",
      diagram: "print",
      index: "04",
      title: "Stampa FDM",
      description:
        "Deposizione controllata strato dopo strato su sistemi Bambu Lab, con parametri coerenti e ripetibili.",
    },
    {
      id: "quality",
      diagram: "quality",
      index: "05",
      title: "Controllo finale",
      description:
        "Verifica dimensionale, rimozione supporti e finitura base prima della spedizione.",
    },
  ],
  specs: [
    { label: "TECNOLOGIA", value: "FDM" },
    { label: "MATERIALE", value: "PLA" },
    { label: "LAYER", value: "0.08–0.28 mm" },
  ],
};

export const MATERIALS = {
  microLabel: "MATERIALI",
  title: "PLA, il nostro materiale principale.",
  description:
    "Rigido, stabile e con finitura pulita. Ideale per prototipi, componenti visivi e piccole serie. Disponibile nei colori standard qui sotto — con altri materiali e colori su richiesta.",
  materialName: "PLA",
  confirmNote:
    "Disponibilità, prezzo e tempi vengono confermati nel preventivo.",
  colors: [
    { name: "Pure White", hex: "#F5F5F5", note: "Opaco" },
    { name: "Absolute Black", hex: "#0B0D10", note: "Opaco" },
    { name: "Milky Pink", hex: "#F2D6DE", note: "Pastello" },
    { name: "Apricot", hex: "#F3C7A6", note: "Pastello" },
    { name: "Baby Blue", hex: "#BFD7EA", note: "Pastello" },
  ],
};

export const QUALITY = {
  microLabel: "PROTOCOLLO QUALITÀ",
  title: "Un controllo per ogni fase.",
  description:
    "I controlli vengono adattati alla tipologia e ai requisiti del pezzo, prima che lasci il laboratorio.",
  items: [
    {
      title: "Verifica dimensionale",
      description: "Le misure principali vengono verificate rispetto al modello di riferimento.",
    },
    {
      title: "Verifica orientamento e supporti",
      description: "Orientamento scelto per resistenza e finitura ottimali.",
    },
    {
      title: "Coerenza del processo",
      description: "Parametri e profili di stampa mantenuti coerenti per un risultato ripetibile.",
    },
    {
      title: "Pulizia e finitura base",
      description: "Rimozione supporti e finitura pulita delle superfici.",
    },
    {
      title: "Imballo protettivo",
      description: "Confezionamento pensato per proteggere il pezzo in transito.",
    },
  ],
};

export const CONFIDENTIALITY = {
  microLabel: "CONFIDENZIALITÀ",
  title: "I tuoi file restano tuoi.",
  description:
    "Trattiamo ogni progetto come riservato. I file servono solo alla valutazione tecnica e alla gestione della tua richiesta.",
  points: [
    {
      title: "Uso limitato",
      description:
        "Il file resta riservato e viene usato solo per la valutazione tecnica e la gestione della richiesta.",
    },
    {
      title: "Nessun uso promozionale",
      description:
        "I file non vengono utilizzati come materiale pubblico o promozionale senza autorizzazione.",
    },
    {
      title: "Responsabilità condivisa",
      description:
        "Il cliente resta responsabile dei file e dei diritti necessari per richiedere la produzione.",
    },
  ],
};

export const REQUEST_STATUS = {
  microLabel: "STATO RICHIESTA",
  title: "Sempre chiaro a che punto sei.",
  description:
    "Ogni richiesta avanza attraverso cinque stati leggibili, dal caricamento iniziale fino alla conferma del pagamento.",
  referenceLabel: "Codice richiesta + email",
  states: [
    { key: "received", label: "Richiesta ricevuta" },
    { key: "analysis", label: "Analisi tecnica" },
    { key: "quote", label: "Preventivo pronto" },
    { key: "awaiting-payment", label: "In attesa di pagamento" },
    { key: "paid", label: "Pagamento ricevuto" },
  ],
};

export const MATERIAL_RENDER = {
  microLabel: "LA RESA DEL MATERIALE",
  title: "Layer, superficie, finitura.",
  description:
    "Come si presenta il PLA da vicino: la trama dei layer FDM, la superficie satinata e la finitura pulita del pezzo finito.",
  points: [
    { title: "Layer FDM", desc: "Layer uniformi da 0.08 a 0.28 mm, per un profilo regolare." },
    { title: "Superficie", desc: "Satinata e opaca, piacevole al tatto." },
    { title: "Texture", desc: "Trama coerente lungo tutta la geometria." },
    { title: "Finitura", desc: "Rimozione supporti e pulizia delle superfici incluse." },
  ],
};

export const QUOTE_JOURNEY = {
  microLabel: "IL PERCORSO",
  title: "Come funziona il preventivo.",
  description:
    "Otto passaggi guidati per trasformare il tuo progetto in una richiesta chiara e completa.",
  cta: "Avvia il preventivo",
  steps: [
    { n: "01", title: "Da cosa parti", desc: "Parti da un file 3D o da un'idea." },
    { n: "02", title: "File o descrizione", desc: "Carichi il file oppure descrivi il progetto." },
    { n: "03", title: "Tipo di pezzo", desc: "Indichi che tipo di pezzo è." },
    { n: "04", title: "Utilizzo e priorità", desc: "Contesto d'uso e obiettivo principale." },
    { n: "05", title: "Materiale, colore, finitura", desc: "Scegli materiale, colore e finitura." },
    { n: "06", title: "Quantità e tempi", desc: "Indichi quantità e tempistica." },
    { n: "07", title: "Contatti", desc: "Lasci un riferimento per la risposta." },
    { n: "08", title: "Riepilogo", desc: "Verifichi i dettagli e invii la richiesta." },
  ],
};

export const FINAL_CTA = {
  microLabel: "INIZIA ORA",
  headline: "Pronto a trasformare il tuo progetto in un pezzo reale?",
  description:
    "Avvia il preventivo e ricevi una valutazione tecnica del tuo progetto.",
  primaryCta: "Avvia il preventivo",
  secondaryCta: "Contattaci",
};

export const FAQ = {
  microLabel: "DOMANDE FREQUENTI",
  title: "Le risposte più utili.",
  items: [
    {
      q: "Posso richiedere un preventivo senza file 3D?",
      a: "Sì. Puoi descrivere il pezzo, indicare misure indicative, funzione e contesto di utilizzo. Ti aiuteremo a capire se serve un file 3D, un riferimento tecnico o un passaggio preliminare.",
    },
    {
      q: "Quali file posso caricare?",
      a: "Il percorso accetta STL, STEP, STP e 3MF. Se hai più versioni del modello, carica quella più aggiornata e aggiungi eventuali note su scala, montaggio o vincoli.",
    },
    {
      q: "Il prezzo è automatico?",
      a: "No. Il preventivo Strato è una valutazione tecnica: una persona controlla file, uso finale, materiale e quantità prima di rispondere con una proposta coerente.",
    },
    {
      q: "Quanto tempo serve per ricevere risposta?",
      a: "Per le richieste inviate dal percorso Preventivo, Strato Lab 3D punta a rispondere entro 12 ore lavorative con una valutazione tecnica personalizzata.",
    },
    {
      q: "Che materiali posso richiedere?",
      a: "Puoi indicare l'utilizzo previsto del pezzo. Strato valuta il materiale più adatto in base a funzione, ambiente, resistenza, finitura e vincoli del file.",
    },
    {
      q: "Posso stampare pezzi funzionali?",
      a: "Sì, se il file e l'applicazione lo consentono. Valutiamo geometria, orientamento, materiale e carichi attesi prima di confermare la produzione.",
    },
    {
      q: "Posso richiedere più pezzi o piccole serie?",
      a: "Sì. Indica quantità, priorità e uso finale. Per piccoli lotti controlliamo ripetibilità, tempi di produzione e coerenza tra i pezzi.",
    },
    {
      q: "Cosa succede se il file non è stampabile?",
      a: "Te lo segnaliamo nella valutazione. Quando possibile indichiamo il problema: scala, spessori, geometrie aperte, dettagli troppo fini o orientamento critico.",
    },
    {
      q: "Come vengono gestiti i file caricati?",
      a: "I file vengono usati per analizzare la richiesta tecnica e preparare il preventivo. Non vengono trattati come contenuti pubblici o materiale promozionale.",
    },
    {
      q: "Come funziona la spedizione?",
      a: "Tempi e costi dipendono da dimensione, materiale, quantità, destinazione e imballaggio. I dettagli vengono confermati nella proposta tecnica.",
    },
  ],
};

export const FOOTER = {
  wordmark: "Strato Lab 3D",
  tagline: "Laboratorio italiano di stampa 3D FDM su richiesta.",
  columns: [
    {
      title: "Servizio",
      links: [
        { label: "Servizi", to: "/servizi" },
        { label: "Materiali", to: "/materiali" },
        { label: "Come funziona", to: "/come-funziona" },
        { label: "Metodo Strato", to: "/metodo" },
        { label: "FAQ", to: "/faq" },
      ],
    },
    {
      title: "Strato Lab 3D",
      links: [
        { label: "Chi siamo", to: "/chi-siamo" },
        { label: "Contatti", to: "/contatti" },
        { label: "Stato richiesta", to: "/stato-richiesta" },
        { label: "Avvia il preventivo", to: "/preventivo" },
      ],
    },
    {
      title: "Legale",
      links: [
        { label: "Privacy", to: "/privacy-policy" },
        { label: "Cookie", to: "/cookie-policy" },
        { label: "Termini e condizioni", to: "/termini-condizioni" },
        { label: "Resi e rimborsi", to: "/resi-rimborsi" },
        { label: "Spedizioni", to: "/spedizioni" },
        { label: "Contatti", to: "/contatti" },
        { label: "Informativa legale", to: "/informativa-legale" },
      ],
    },
  ],
};
